import { HangoutState } from '../../hangout/state/HangoutState.js';
import { getScenarioBackgroundImage } from '../../../core/utils/hangoutUtils.js';
import { storage } from '../../../js/utils/storage.js';
import { applyTranslations, getCurrentLanguage } from '../../../js/translations.js';
import { isCharacterBlocked, isScenarioBlocked } from '../../../js/moderation/blockedContentStore.js';
import {
  resolveConversationBackdrop,
  resolveScenarioDisplayText,
  overlaySelectedCharacterSlots
} from '../services/scenarioMappers.js';
import {
  reconstructFinalStateFromConversation,
  normalizeScenarioId,
  resolveConversationScenarioName
} from './state-utils.js';
import { buildRecentChatCard } from './recent-chat-card.js';
import { resumeHangoutFromConversation } from './resume-hangout.js';

const PAGE_LIMIT = 20;

const isUserLoggedIn = () => {
  try {
    const userData = storage.get('userData') || {};
    return userData.isLoggedIn === true && !!(window.tokenManager?.getAccessToken?.() ?? null);
  } catch {
    return false;
  }
};

const isVisibleConversation = (conversation) => {
  if (!conversation?.conversationId) return false;
  if (isCharacterBlocked(conversation.characterId)) return false;
  if (isScenarioBlocked(conversation.scenarioId)) return false;
  return true;
};

export class RecentChatsView {
  constructor(element, { state, service, onHasMoreChange } = {}) {
    this.element = element;
    this.rankingsState = state;
    this.service = service;
    this.onHasMoreChange = onHasMoreChange;
    this.scenarioDetailsCache = new Map();
    this.loadGeneration = 0;
    this.nextCursor = null;
    this.hasMore = false;
    this.isLoading = false;
    this.isLoadingMore = false;
    this.sessionLoaded = false;
    this.shownConversationIds = new Set();
    this.handleLoginClick = this.handleLoginClick.bind(this);
    this.handleAuthChanged = this.handleAuthChanged.bind(this);
  }

  start() {
    document.addEventListener('auth:login:success', this.handleAuthChanged);
    document.addEventListener('auth:logout', this.handleAuthChanged);
  }

  destroy() {
    document.removeEventListener('auth:login:success', this.handleAuthChanged);
    document.removeEventListener('auth:logout', this.handleAuthChanged);
    this.element?.replaceChildren();
    this.scenarioDetailsCache.clear();
    this.resetSession();
  }

  resetSession() {
    this.sessionLoaded = false;
    this.isLoading = false;
    this.isLoadingMore = false;
    this.nextCursor = null;
    this.shownConversationIds.clear();
    this.loadGeneration += 1;
    this.setHasMore(false);
  }

  setHasMore(value) {
    this.hasMore = Boolean(value);
    this.onHasMoreChange?.(this.hasMore);
  }

  handleAuthChanged() {
    this.resetSession();
    if (this.element && !this.element.hidden) {
      void this.load();
    }
  }

  async handleLoginClick() {
    try {
      const { default: loginModalController } = await import('../../../js/components/LoginModalController.js');
      loginModalController?.show?.();
    } catch (error) {
      console.error('RecentChatsView: failed to open login', error);
    }
  }

  async getScenarioDetailsCached(spotId) {
    if (!spotId) return null;
    const cacheKey = String(spotId);
    if (this.scenarioDetailsCache.has(cacheKey)) return this.scenarioDetailsCache.get(cacheKey);

    const requestPromise = this.service.getScenarioDetails(spotId)
      .catch((error) => {
        this.scenarioDetailsCache.delete(cacheKey);
        throw error;
      });

    this.scenarioDetailsCache.set(cacheKey, requestPromise);
    return requestPromise;
  }

  renderStatus(kind) {
    this.element.replaceChildren();
    const status = document.createElement('div');
    status.className = 'recent-chats-status';

    const message = document.createElement('p');
    message.className = 'recent-chats-status__message';

    if (kind === 'loading') {
      message.setAttribute('data-translate', 'rankings.recentChatsLoading');
      message.textContent = 'Loading recent chats…';
      status.appendChild(message);
    } else if (kind === 'login') {
      message.setAttribute('data-translate', 'rankings.recentChatsLoginPrompt');
      message.textContent = 'Log in to see your recent chats.';
      const loginButton = document.createElement('button');
      loginButton.type = 'button';
      loginButton.className = 'recent-chats-login-btn';
      loginButton.setAttribute('data-translate', 'rankings.recentChatsLoginButton');
      loginButton.textContent = 'Log in';
      loginButton.addEventListener('click', this.handleLoginClick);
      status.appendChild(message);
      status.appendChild(loginButton);
    } else if (kind === 'error') {
      message.setAttribute('data-translate', 'rankings.recentChatsError');
      message.textContent = 'Could not load recent chats.';
      status.appendChild(message);
    } else {
      message.setAttribute('data-translate', 'rankings.recentChatsEmpty');
      message.textContent = 'No recent chats yet.';
      status.appendChild(message);
    }

    this.element.appendChild(status);
    const currentLang = getCurrentLanguage();
    if (currentLang) applyTranslations('rankings', currentLang);
  }

  async load() {
    if (this.sessionLoaded || this.isLoading) {
      this.setHasMore(Boolean(this.nextCursor));
      return;
    }

    this.isLoading = true;
    const generation = ++this.loadGeneration;
    this.nextCursor = null;
    this.isLoadingMore = false;
    this.shownConversationIds.clear();
    this.setHasMore(false);
    this.renderStatus('loading');

    const hangoutState = HangoutState.getInstance();
    let conversations = [];
    let nextCursor = null;
    try {
      const result = await hangoutState.getRecentConversations({ limit: PAGE_LIMIT });
      conversations = result?.conversations || [];
      nextCursor = result?.nextCursor || null;
      if (generation !== this.loadGeneration) return;

      const visibleConversations = (conversations || []).filter(isVisibleConversation);

      if (visibleConversations.length === 0) {
        this.renderStatus(isUserLoggedIn() ? 'empty' : 'login');
        this.nextCursor = null;
        this.setHasMore(false);
        this.sessionLoaded = true;
        return;
      }

      await this.renderCards(visibleConversations, generation, { append: false });
      if (generation !== this.loadGeneration) return;
      this.nextCursor = nextCursor;
      this.setHasMore(Boolean(nextCursor));
      this.sessionLoaded = true;
    } catch (error) {
      console.error('RecentChatsView.load: conversations', error);
      if (generation !== this.loadGeneration) return;
      this.renderStatus(isUserLoggedIn() ? 'error' : 'login');
      this.nextCursor = null;
      this.setHasMore(false);
      this.sessionLoaded = false;
    } finally {
      if (generation === this.loadGeneration) {
        this.isLoading = false;
      }
    }
  }

  async loadMore() {
    if (!this.hasMore || !this.nextCursor || this.isLoadingMore) return;
    if (!isUserLoggedIn()) return;

    const generation = this.loadGeneration;
    this.isLoadingMore = true;
    const hangoutState = HangoutState.getInstance();
    let conversations = [];
    let nextCursor = null;
    try {
      const result = await hangoutState.getRecentConversations({
        limit: PAGE_LIMIT,
        cursor: this.nextCursor
      });
      conversations = result?.conversations || [];
      nextCursor = result?.nextCursor || null;
      if (generation !== this.loadGeneration) return;

      const visibleConversations = (conversations || [])
        .filter(isVisibleConversation)
        .filter((conversation) => !this.shownConversationIds.has(conversation.conversationId));

      if (visibleConversations.length > 0) {
        await this.renderCards(visibleConversations, generation, { append: true });
      }
      if (generation !== this.loadGeneration) return;
      this.nextCursor = nextCursor;
      this.setHasMore(Boolean(nextCursor));
    } catch (error) {
      console.error('RecentChatsView.loadMore: conversations', error);
    } finally {
      if (generation === this.loadGeneration) {
        this.isLoadingMore = false;
      }
    }
  }

  async renderCards(conversations, generation, { append = false } = {}) {
    let allScenarios = Array.isArray(this.rankingsState?.scenarios)
      ? this.rankingsState.scenarios
      : [];
    if (!allScenarios.length && this.service?.getScenarios) {
      try {
        const scenariosResponse = await this.service.getScenarios();
        allScenarios = Array.isArray(scenariosResponse?.data) ? scenariosResponse.data : [];
      } catch (error) {
        console.warn('RecentChatsView: failed to preload scenarios', error);
        allScenarios = [];
      }
    }
    if (generation !== this.loadGeneration) return;

    const uniqueScenarioIds = [...new Set(
      conversations
        .map((conversation) => conversation.scenarioId)
        .filter((scenarioId) => scenarioId !== undefined && scenarioId !== null && String(scenarioId) !== '')
        .map((scenarioId) => String(scenarioId))
    )];
    await Promise.all(uniqueScenarioIds.map((scenarioId) => (
      this.getScenarioDetailsCached(scenarioId).catch(() => null)
    )));
    if (generation !== this.loadGeneration) return;

    const scenarioNameById = new Map(
      allScenarios
        .filter((scenario) => scenario && scenario.id != null && typeof scenario.name === 'string' && scenario.name.trim())
        .map((scenario) => [String(scenario.id), scenario.name.trim()])
    );

    if (!append) {
      this.element.replaceChildren();
      this.shownConversationIds.clear();
    }

    const cards = [];
    for (const conversation of conversations) {
      if (this.shownConversationIds.has(conversation.conversationId)) continue;
      let backgroundImage;
      let scenarioDetails = null;
      try {
        const finalState = reconstructFinalStateFromConversation(conversation);
        if (conversation.scenarioId) {
          scenarioDetails = await this.getScenarioDetailsCached(conversation.scenarioId);
        }
        const { locId, timeOfDay } = resolveConversationBackdrop(scenarioDetails, finalState);
        backgroundImage = await getScenarioBackgroundImage(null, locId, timeOfDay);
      } catch {
        backgroundImage = await getScenarioBackgroundImage(null, 0, 'night');
      }
      if (generation !== this.loadGeneration) return;

      const conversationChars = Array.isArray(conversation.allCharacters) ? conversation.allCharacters : [];
      const scenarioChars = Array.isArray(scenarioDetails?.chars) ? scenarioDetails.chars : [];
      const displayChars = overlaySelectedCharacterSlots(scenarioChars, conversationChars);
      const mappedScenarioName = resolveScenarioDisplayText(
        scenarioNameById.get(normalizeScenarioId(conversation.scenarioId)) || '',
        displayChars
      );
      const resolvedScenarioName = resolveConversationScenarioName({
        mappedName: mappedScenarioName,
        detailName: resolveScenarioDisplayText(scenarioDetails?.name, displayChars),
        conversationName: conversation.scenarioName
      });

      const card = buildRecentChatCard(
        { ...conversation, scenarioName: resolvedScenarioName },
        {
          backgroundImage,
          listedCharacters: this.rankingsState?.characters,
          onCardClick: (selected) => {
            void resumeHangoutFromConversation({
              conversation: selected,
              service: this.service,
              rankingsState: this.rankingsState
            });
          }
        }
      );
      cards.push(card);
      this.shownConversationIds.add(conversation.conversationId);
    }

    if (generation !== this.loadGeneration) return;
    if (append) {
      this.element.append(...cards);
      return;
    }
    this.element.replaceChildren(...cards);
  }
}
